import { useState } from "react"
import { Home, Database, LogOut, ChevronDown, Cog, Play, List, SquarePen } from "lucide-react"
import { usePage } from "@inertiajs/react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SourceFavicon } from "@/components/source_favicon"

import { Link } from "@inertiajs/react"

import * as api from "@/api"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog"

export function AppSidebar() {
  const { organization, user } = usePage().props.auth
  const currentPath = window.location.pathname
  const [videoModalOpen, setVideoModalOpen] = useState(false)

  const canSwitchOrganizations = !!user?.admin || (user?.organizations?.length || 0) > 1

  const handleOrganizationChange = (orgNanoId) => {
    window.location.href = api.organizationsDashboard.show.path({ org_nano_id: orgNanoId })
  }

  const isActive = (url) => {
    return currentPath === url
  }

  const recurringSourcesItem = {
    title: "Recurring Sources",
    url: api.organizationsHighlights.index.path({ org_nano_id: organization.nano_id }),
    disabled: false,
  }

  const ideasItem = {
    title: "Your Ideas",
    url: `/${organization.nano_id}/highlights/ideas`,
    disabled: false,
  }

  const calendarPostsItem = {
    title: "Calendar View",
    url: api.organizationsContent.show.path({ org_nano_id: organization.nano_id }),
    disabled: false,
  }

  const listPostsItem = {
    title: "List View",
    url: api.organizationsContentListView.show.path({ org_nano_id: organization.nano_id }),
    disabled: false,
  }

  const items = [
    {
      title: "Home",
      url: "/",
      icon: Home,
      disabled: false
    },
    // {
    //   title: "Content Strategy",
    //   url: api.organizationsContentStrategy.show.path({ org_nano_id: organization.nano_id }),
    //   icon: Inbox,
    //   disabled: false
    // },
  ]

  const styleGuideItem = {
    title: "Style Prompt",
    url: api.organizationsStyleGuide.show.path({ org_nano_id: organization.nano_id }),
    disabled: false
  }

  const integrationsItem = {
    title: "Integrations",
    url: api.organizationsIntegrations.show.path({ org_nano_id: organization.nano_id }),
    disabled: false
  }

  const contentSourcesItems = [recurringSourcesItem, ideasItem]
  const postsItems = [calendarPostsItem, listPostsItem]
  const settingsItems = [styleGuideItem, integrationsItem]

  const hasActiveItem = (sectionItems) => sectionItems.some((item) => isActive(item.url))

  const [openSections, setOpenSections] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("sidebar_sections") || "{}")
      return {
        contentSources: stored.contentSources ?? hasActiveItem(contentSourcesItems),
        posts: stored.posts ?? hasActiveItem(postsItems),
        settings: stored.settings ?? hasActiveItem(settingsItems),
      }
    } catch {
      return {
        contentSources: hasActiveItem(contentSourcesItems),
        posts: hasActiveItem(postsItems),
        settings: hasActiveItem(settingsItems),
      }
    }
  })

  const toggleSection = (section) => {
    setOpenSections((prev) => {
      const next = { ...prev, [section]: !prev[section] }
      try { localStorage.setItem("sidebar_sections", JSON.stringify(next)) } catch {}
      return next
    })
  }

  const iconStrokeWidth = 1.75

  return (
    <Sidebar>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="justify-between gap-2 pr-1">
            {canSwitchOrganizations ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="group flex min-w-0 items-center gap-1.5 truncate text-sm font-medium text-sidebar-foreground/70 hover:text-white transition-colors outline-none">
                  <SourceFavicon url={organization.website_url} name={organization.name} className="size-4 rounded-full shrink-0" />
                  <span className="min-w-0 truncate">{organization.name}</span>
                  <ChevronDown className="h-3 w-3 shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" strokeWidth={iconStrokeWidth} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {user.organizations.map((org) => (
                    <DropdownMenuItem
                      key={org.nano_id}
                      onSelect={() => handleOrganizationChange(org.nano_id)}
                      className={org.nano_id === organization.nano_id ? "font-medium" : ""}
                    >
                      {org.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <span className="flex min-w-0 items-center gap-1.5 truncate text-sm font-medium">
                <SourceFavicon url={organization.website_url} name={organization.name} className="size-4 rounded-full shrink-0" />
                <span className="min-w-0 truncate">{organization.name}</span>
              </span>
            )}
            <Link
              href={api.organizationsPosts.new.path({ org_nano_id: organization.nano_id })}
              className="flex size-6 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              aria-label="Create new post"
              title="Create new post"
            >
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                <SquarePen className="h-3.5 w-3.5 text-white" strokeWidth={iconStrokeWidth} />
              </div>
              <span className="sr-only">Create new post</span>
            </Link>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    className={cn(isActive(item.url) ? "bg-sidebar-primary hover:bg-sidebar-primary text-sidebar-primary-foreground" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", item.disabled ? "pointer-events-none opacity-50 cursor-not-allowed" : "")}
                  >
                    <Link href={item.url}>
                      <item.icon className="h-3.5 w-3.5" strokeWidth={iconStrokeWidth} />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              <Collapsible open={openSections.contentSources} onOpenChange={() => toggleSection("contentSources")} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton className="flex justify-between items-center w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                      <div className="flex items-center">
                        <Database className="mr-2 h-3.5 w-3.5" strokeWidth={iconStrokeWidth} />
                        <span>Content Sources</span>
                      </div>
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-100 transition-opacity duration-200 group-data-[state=open]/collapsible:opacity-0" strokeWidth={iconStrokeWidth} />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuButton
                          variant="ghost"
                          asChild
                          className={cn(isActive(recurringSourcesItem.url) ? "bg-sidebar-primary hover:bg-sidebar-primary text-sidebar-primary-foreground" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", recurringSourcesItem.disabled ? "pointer-events-none opacity-50 cursor-not-allowed" : "")}
                        >
                          <Link href={recurringSourcesItem.url}>
                            <span>{recurringSourcesItem.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuButton
                          variant="ghost"
                          asChild
                          className={cn(isActive(ideasItem.url) ? "bg-sidebar-primary hover:bg-sidebar-primary text-sidebar-primary-foreground" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", ideasItem.disabled ? "pointer-events-none opacity-50 cursor-not-allowed" : "")}
                        >
                          <Link href={ideasItem.url}>
                            <span>{ideasItem.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              <Collapsible open={openSections.posts} onOpenChange={() => toggleSection("posts")} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton className="flex justify-between items-center w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                      <div className="flex items-center">
                        <List className="mr-2 h-3.5 w-3.5" strokeWidth={iconStrokeWidth} />
                        <span>Posts</span>
                      </div>
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-100 transition-opacity duration-200 group-data-[state=open]/collapsible:opacity-0" strokeWidth={iconStrokeWidth} />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuButton
                          variant="ghost"
                          asChild
                          className={cn(isActive(calendarPostsItem.url) ? "bg-sidebar-primary hover:bg-sidebar-primary text-sidebar-primary-foreground" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", calendarPostsItem.disabled ? "pointer-events-none opacity-50 cursor-not-allowed" : "")}
                        >
                          <Link href={calendarPostsItem.url}>
                            <span>{calendarPostsItem.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuButton
                          variant="ghost"
                          asChild
                          className={cn(isActive(listPostsItem.url) ? "bg-sidebar-primary hover:bg-sidebar-primary text-sidebar-primary-foreground" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", listPostsItem.disabled ? "pointer-events-none opacity-50 cursor-not-allowed" : "")}
                        >
                          <Link href={listPostsItem.url}>
                            <span>{listPostsItem.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              <Collapsible open={openSections.settings} onOpenChange={() => toggleSection("settings")} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton className="flex justify-between items-center w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                      <div className="flex items-center">
                        <Cog className="mr-2 h-3.5 w-3.5" strokeWidth={iconStrokeWidth} />
                        <span>Settings</span>
                      </div>
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-100 transition-opacity duration-200 group-data-[state=open]/collapsible:opacity-0" strokeWidth={iconStrokeWidth} />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuButton
                          variant="ghost"
                          asChild
                          className={cn(isActive(styleGuideItem.url) ? "bg-sidebar-primary hover:bg-sidebar-primary text-sidebar-primary-foreground" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", styleGuideItem.disabled ? "pointer-events-none opacity-50 cursor-not-allowed" : "")}
                        >
                          <Link href={styleGuideItem.url}>
                            <span>{styleGuideItem.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuButton
                          variant="ghost"
                          asChild
                          className={cn(isActive(integrationsItem.url) ? "bg-sidebar-primary hover:bg-sidebar-primary text-sidebar-primary-foreground" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", integrationsItem.disabled ? "pointer-events-none opacity-50 cursor-not-allowed" : "")}
                        >
                          <Link href={integrationsItem.url}>
                            <span>{integrationsItem.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuSubItem>
                      {organization.subscribed && (
                        <SidebarMenuSubItem>
                          <SidebarMenuButton
                            variant="ghost"
                            asChild
                          >
                            <a href="https://billing.stripe.com/p/login/dR6eWL5fh1Or0JG000" target="_blank" rel="noopener noreferrer">
                              <span>Manage Subscription</span>
                            </a>
                          </SidebarMenuButton>
                        </SidebarMenuSubItem>
                      )}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {/* Free Posts Counter & Upgrade */}
        {!organization.subscribed && (
          <SidebarGroup className="mt-auto">
            <SidebarGroupContent>
              <div className="px-3 py-2">
                <div className="text-xs text-muted-foreground mb-2 text-center">
                  {organization.free_posts_remaining > 0 ? (
                    <>
                      <span className="font-medium">{organization.free_posts_remaining}</span> of {organization.free_post_limit} free posts remaining
                    </>
                  ) : (
                    <span className="font-medium">Free posts limit reached</span>
                  )}
                </div>
                <Progress 
                  value={((organization.free_post_limit - organization.free_posts_remaining) / organization.free_post_limit) * 100} 
                  className="mb-3 h-1 bg-muted/60"
                  indicatorClassName="bg-foreground/40"
                />
                <Button
                  size="sm"
                  className="w-full"
                  asChild
                >
                  <Link href={api.organizationsUpgrade.show.path({ org_nano_id: organization.nano_id })}>
                    Upgrade
                  </Link>
                </Button>
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Intro Video */}
        <SidebarGroup className={organization.subscribed ? "mt-auto" : ""}>
          <SidebarGroupContent>
            <div className="px-2 pb-2">
              <button
                type="button"
                onClick={() => setVideoModalOpen(true)}
                className="relative w-full overflow-hidden rounded-xl group border p-1"
              >
                <img
                  src="/intro-thumb.jpg"
                  alt="Watch intro video"
                  className="w-full aspect-video object-cover rounded-lg"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
                <div className="hidden w-full aspect-video bg-muted items-center justify-center">
                  <Play className="h-8 w-8 text-white fill-white" strokeWidth={iconStrokeWidth} />
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                  <Play className="h-8 w-8 text-white fill-white drop-shadow-lg" strokeWidth={iconStrokeWidth} />
                </div>
              </button>
            </div>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Video Modal */}
        <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
          <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden">
            <video
              controls
              autoPlay
              className="w-full aspect-video"
              src="https://pub-bcae04c90dfe4563b62b1cc6f1a611b7.r2.dev/fm%20demo%20v1.mp4"
            />
          </DialogContent>
        </Dialog>
        {/* Sign Out Button */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link href="/logout" method="delete" as="button" className="w-full">
                    <LogOut className="h-3.5 w-3.5" strokeWidth={iconStrokeWidth} />
                    <span>Sign Out</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}
